import {request} from "./request";
import * as PATHS from "@/constants/path"
import {URLParamBuilder} from "@/utils/functions";
import {JourneyModel} from "../../typings/models/Journey";


// region Fetch Journey
type FetchJourneyRequestOptions = {
  params: {
    journeyId: JourneyModel["hashId"]
  },
}

export function fetchJourneyConfigRequest({
                                            params,
                                          }: FetchJourneyRequestOptions) {
  const url = URLParamBuilder(PATHS.GET_JOURNEY_CONFIG, {
    params
  })
  return request(url, {
    method: 'GET'
  });
}

// endregion

// region Edit Configuration
type EditConfigurationRequestOptions = {
  params: {
    journeyId: JourneyModel["hashId"],
    configurationKey: string,
  },
  data: any,
  /**
   * `PUT` replaces the stored configuration object, `PATCH` deep-merges the
   * payload into it (both are routed to the same endpoint, see
   * Configuration::updateOrCreate on the API side).
   *
   * Prefer `PATCH` whenever the caller knows only part of the config: the value
   * read back from `GET /journeys/:id` is the *effective* one — class defaults,
   * then the partner's default lead source, then the journey's own row on top —
   * so writing it back in full copies inherited values into the journey's own
   * row and silently unsubscribes it from later changes to those defaults.
   */
  method?: 'PUT' | 'PATCH',
}

export function editJourneyConfigurationRequest({
                                                  params,
                                                  data,
                                                  method = 'PUT'
                                                }: EditConfigurationRequestOptions) {
  const url = URLParamBuilder(PATHS.EDIT_JOURNEY_CONFIG, {
    params
  })
  return request(url, {
    method,
    data
  });
}

// endregion

// region Configuration scope (default lead source vs a journey's own row)
/**
 * Which of the partner's lead sources own a configuration row for the given
 * keys, and which inherit the default lead source's.
 *
 * One call for every lead source rather than one per journey: the Touchpoints
 * scope selector shows the whole list's state at once.
 */
export function fetchJourneyConfigurationScopesRequest({
  keys = [],
}: {
  keys?: string[];
} = {}) {
  const url = URLParamBuilder(PATHS.JOURNEY_CONFIGURATION_SCOPES, {
    queryString: keys.length ? { keys: keys.join(',') } : {},
  });
  return request<any>(url, { method: 'GET' });
}

/**
 * Give a journey a configuration of its own for one key, copied from the
 * partner's default lead source.
 *
 * Server-side rather than a PUT of the values the page already holds: what the
 * journey reads today is the default's *stored* row, and the merged
 * configuration the Back Office has is not that row. Copying it there means
 * deviating changes nothing until someone edits. Idempotent.
 */
export function deviateJourneyConfigurationRequest({
  params,
}: {
  params: {
    journeyId: JourneyModel['hashId'];
    configurationKey: string;
  };
}) {
  const url = URLParamBuilder(PATHS.DEVIATE_JOURNEY_CONFIG, { params });
  return request<any>(url, { method: 'POST' });
}

/**
 * Drop a journey's own row for one config key so it follows the partner's
 * default lead source again.
 *
 * The only way back: writing the config is what made the journey deviate, and
 * emptying the value does not undo it — an empty row still wins the merge,
 * which is what "emails deactivated" means. 422 on the default lead source
 * itself, which has nothing to fall back to.
 */
export function deleteJourneyConfigurationRequest({
  params,
}: {
  params: {
    journeyId: JourneyModel['hashId'];
    configurationKey: string;
  };
}) {
  const url = URLParamBuilder(PATHS.EDIT_JOURNEY_CONFIG, { params });
  return request<any>(url, { method: 'DELETE' });
}

// endregion
